/**
 * Why the matching rules left a credit in the attribution queue, in staff words, and what can be
 * done with it (#311). The codes are the ones the backend records in
 * `bank_transactions.auto_match_refusal`: `self_service_funding.REFUSED_*`,
 * `attribution_eligibility.REFUSED_*`, `receiving_account.REFUSED_*`,
 * `funding_rails.REFUSED_RAIL_NOT_ACCEPTED` and `bank_statements.SUB_CENT_REFUSAL`. Neutral on
 * purpose: it names a state, never why an account is suspended or frozen.
 */
export interface QueueRefusal {
  reason: string;
  action: string;
}

export const QUEUE_REFUSALS: Record<string, QueueRefusal> = {
  rail_not_accepted: {
    reason: 'It did not arrive by bank wire.',
    action: 'It cannot be credited: a manager sends it back to where it came from.',
  },
  sub_cent_amount: {
    reason: 'The amount the bank reported needs a manager’s check.',
    action: 'A manager compares it with the bank statement before it is attributed by hand.',
  },
  ambiguous_reference: {
    reason: 'It carries more than one funding reference.',
    action: 'Attribute it by hand to the client and product it belongs to.',
  },
  unknown_reference: {
    reason: 'Its funding reference matches no active reference.',
    action: 'Find the client from the sender and attribute it by hand, or send it back.',
  },
  client_inactive: {
    reason: 'The client’s account is not active.',
    action: 'Attribute it to the client who sent it, or send it back.',
  },
  client_suspended: {
    reason: 'The client’s account is suspended.',
    action: 'It cannot be attributed until the suspension is lifted. A manager may send it back.',
  },
  client_frozen: {
    reason: 'The client’s account is frozen for money movements.',
    action: 'It cannot be attributed until the freeze is lifted. A manager may send it back.',
  },
  product_inactive: {
    reason: 'The product is not open to this client and they hold no contract for it.',
    action: 'Attribute it to a product the client holds a contract for, or send it back.',
  },
  product_not_fundable: {
    reason: 'The product cannot receive money yet.',
    action: 'Ask an administrator to finish the product’s fund set-up, then attribute it by hand.',
  },
  funds_unavailable: {
    reason: 'The fund system did not answer when it arrived.',
    action: 'Attribute it by hand now.',
  },
  currency_mismatch: {
    reason: 'It is in a different currency from the product.',
    action: 'Attribute it to a product in its currency, or send it back.',
  },
  no_active_contract: {
    reason: 'The client has no active contract for the product.',
    action: 'Attribute it once the client’s contract is active. A manager may send it back.',
  },
  sender_account_missing: {
    reason: 'The bank did not report the account it came from.',
    action: 'Attribute it by hand, naming the client’s account shown on their transfer receipt.',
  },
  sender_not_registered: {
    reason: 'It came from an account that is not registered to the client.',
    action: 'Attribute it once the client registers that account as their own. A manager may send it back.',
  },
  source_account_refused: {
    reason: 'It came from an account a manager refused as not the client’s own.',
    action: 'It cannot be credited: a manager sends it back to the account it came from.',
  },
  monthly_cap_exceeded: {
    reason: 'It is above the client’s monthly limit for the product.',
    action: 'A person decides: attribute it by hand or send it back.',
  },
  received_in_another_fund_account: {
    reason: 'It arrived in the bank account of another fund.',
    action:
      'It cannot be credited to this product: a manager sends it back, and the client transfers to the account on their contribution page.',
  },
  fund_not_receiving: {
    reason: 'The product’s fund is full and no other series is open.',
    action: 'Ask the fund administrator whether a new series opens; otherwise send it back.',
  },
};

const UNKNOWN: QueueRefusal = {
  reason: 'It was not matched automatically.',
  action: 'Attribute it by hand or send it back.',
};

/** What the card says for a credit the rules left in the queue, or null when no rule refused it
 *  (a line keyed by hand, or one that carried no reference). Never shows a raw code. */
export function queueRefusal(code: string | null | undefined): QueueRefusal | null {
  if (!code) return null;
  return QUEUE_REFUSALS[code] ?? UNKNOWN;
}
